"use client";

import { useEffect, useRef } from "react";
import { register, unregister } from "./registry";
import type { Binding } from "./types";

/**
 * Registers a shortcut for as long as the component is mounted — for keys that
 * only make sense while a particular dialog or panel is open.
 *
 * The handler is held in a ref so the binding is registered once and never
 * churns on re-render, while `run` still sees current props and state.
 */
export function useShortcut(binding: Binding): void {
  const runRef = useRef(binding.run);
  const whenRef = useRef(binding.when);

  runRef.current = binding.run;
  whenRef.current = binding.when;

  const { id, keys, scope, label, group, requiresEditable, allowInInput, viaProseMirror } =
    binding;

  // `keys` may be a {apple, other} object, which is a fresh identity on every
  // render. Depending on it directly would unregister and re-register the
  // binding on each one.
  const keysDep = typeof keys === "string" ? keys : `${keys.apple}|${keys.other}`;

  useEffect(() => {
    register({
      id,
      keys,
      scope,
      label,
      group,
      requiresEditable,
      allowInInput,
      viaProseMirror,
      run: (ctx) => runRef.current(ctx),
      when: (ctx) => (whenRef.current ? whenRef.current(ctx) : true),
    });
    return () => unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, keysDep, scope, label, group, requiresEditable, allowInInput, viaProseMirror]);
}
